import React, { useEffect, useState } from 'react';
import { paramCase } from 'param-case';
import LoaderIcon from 'uswds/dist/img/loader.svg';
import { ErrorMessage, Fieldset, Form, FormGroup, Hint, Icon, Legend } from '@ver-uds/uswds-react';
import { Button, IllustratedRadioButton, Tag } from '@ver-uds/react';
import { FormProvider, useForm } from 'react-hook-form';
import { AccountCategory, CategoryType, CompanyInfo, SubCategoryType, VerificationEnumType } from 'eve-account-client';
import { Link } from 'react-router-dom';
import { useEmployerCategoriesQuery, useVerificationTypesQuery } from '../../../services';
import EnrollmentStepperButtons from '../EnrollmentStepperButtons';
import UEIField from '../../UEIField';
import DUNSField from '../../DUNSField';
import CategoryIcon from './CategoryIcon';
import HelpModalContent from '../../../constants/HelpModalContent';
import HelpModal, { HelpModalProps } from '../../HelpModal';
import FormVariant from '../FormVariant';
import EVerifyEmail from '../EVerifyEmail';

export type EmployerCategoryFormData = {
  employerCategory: AccountCategory;
  ueiSAM?: string;
  dunsNumber?: string;
};

export type FormValues = {
  tier1: string;
  tier2?: string;
  tier3?: string;
  tier4?: string;
  ueiSAM?: string;
  dunsNumber?: string;
};

export interface EmployerCategoryFormProps {
  variantType: FormVariant;
  // (Case: Enrollment)
  employerCategory?: AccountCategory;
  dunsNumber?: string;
  ueiSAM?: string;
  isEditMode?: boolean;
  // (Case: Edit)
  cancelUrl?: string;
  companyInfoData?: CompanyInfo | undefined;
  onSubmitForm: (data: EmployerCategoryFormData) => void;
}

const EmployerCategoryForm = ({
  variantType,
  employerCategory,
  dunsNumber,
  ueiSAM,
  isEditMode = false,
  cancelUrl,
  companyInfoData,
  onSubmitForm,
}: EmployerCategoryFormProps): JSX.Element => {
  const { isLoading: employerCategoriesIsLoading, data: employerCategoriesData } = useEmployerCategoriesQuery();

  const displayCompanyName = variantType === FormVariant.ENROLLMENT ? 'your company' : companyInfoData?.companyName;
  const category = employerCategoriesData?.find(({ value }) => value === employerCategory?.category);

  // eslint-disable-next-line @typescript-eslint/unbound-method
  const formMethods = useForm<FormValues>({
    mode: 'onTouched',
    reValidateMode: 'onChange',
    defaultValues: {
      tier1: category?.groupName ?? category?.value,
      tier2: category?.groupName ? category?.value : '',
      tier3: employerCategory?.subCategory,
      tier4: employerCategory?.employeeVerificationType,
      ueiSAM: companyInfoData?.ueiSAM,
      dunsNumber: companyInfoData?.dunsNumber,
    },
  });

  // eslint-disable-next-line @typescript-eslint/unbound-method
  const { register, reset, watch, errors, handleSubmit } = formMethods;

  const { tier1, tier2, tier3, tier4 } = watch();
  const { isLoading: verificationTypesIsLoading, data: verificationTypesData } = useVerificationTypesQuery(tier3);

  const ueiSAMRequired = Boolean(
    tier4 && tier2 === CategoryType.FEDERALCONTRACTOR && !companyInfoData?.ueiSAM && !ueiSAM,
  );
  const renderDunsNumber = Boolean(
    tier4 && tier2 === CategoryType.FEDERALCONTRACTOR && !companyInfoData?.dunsNumber && !dunsNumber,
  );

  // Help Modal setup
  const [modalContent, setModalContent] = useState<HelpModalProps['modalContent']>({ header: '', body: <></> });
  const [helpModalIsOpen, setHelpModalIsOpen] = useState(false);
  const openModal = (content: HelpModalProps['modalContent']) => {
    setModalContent(content);
    setHelpModalIsOpen(true);
  };

  // determine modal text for helpModal
  function determineHelpModalText(value: string, tier: number) {
    switch (value) {
      case CategoryType.NONEOFTHEABOVE:
      case SubCategoryType.NONEOFTHEABOVE:
        if (tier === 1) {
          return openModal(HelpModalContent.noneOfTheAbove);
          // eslint-disable-next-line no-else-return
        } else {
          return openModal(HelpModalContent.noneOfTheAboveTier3);
        }
      case 'Federal Contractor':
        return openModal(HelpModalContent.federalContractor);

      case 'Government':
        return openModal(HelpModalContent.government);

      case CategoryType.AIRPORTOPERATORS:
        return openModal(HelpModalContent.airportOperator);

      case CategoryType.FEDERALCONTRACTOR:
        return openModal(HelpModalContent.federalContractorWithFarEverifyClause);

      case CategoryType.FEDERALCONTRACTORWOEVCLAUSE:
        return openModal(HelpModalContent.federalContractorWithoutFarEverifyClause);

      case CategoryType.FEDERALGOVERNMENT:
        return openModal(HelpModalContent.federalGovernment);

      case CategoryType.STATEGOVERNMENT:
        return openModal(HelpModalContent.stateGovernment);

      case CategoryType.LOCALGOVERNMENT:
        return openModal(HelpModalContent.localGovernment);

      case SubCategoryType.INSTITUTEOFHIGHERLEARNING:
        return openModal(HelpModalContent.instituteOfHigherLearning);

      case SubCategoryType.STATEORLOCALGOVT:
        return openModal(HelpModalContent.stateOrLocalGovernment);

      case SubCategoryType.NATIVEAMERICANTRIBE:
        return openModal(HelpModalContent.federallyRecognizedIndianTribe);

      case SubCategoryType.SURETY:
        return openModal(HelpModalContent.sureties);

      // part of the 'none of these...' tier 4 options (there are two)
      case VerificationEnumType.NEWANDEXISTINGHIREONFEDCONT:
        return openModal(HelpModalContent.newHiresAndAssignedToACoveredFederalContract);

      case VerificationEnumType.ENTIREWORKFORCE:
        return openModal(HelpModalContent.entireWorkforce);

      case VerificationEnumType.ALLNEWANDEXISTINGFEDCONTHIRE:
        return openModal(HelpModalContent.allNewHiresAndAssignedToACoveredFederalContract);

      case VerificationEnumType.FEDCONTEMPLOYEESONLY:
        return openModal(HelpModalContent.assignedToACoveredFederalContractOnly);

      default:
        return openModal(HelpModalContent.error);
    }
  }

  const displayUeiSAM = (variantType === FormVariant.EDIT || isEditMode) && ueiSAMRequired && openModal;
  const displayDunsNumber = (variantType === FormVariant.EDIT || isEditMode) && renderDunsNumber && openModal;

  // Set the default form values when we've loaded them.
  useEffect(() => {
    let data: AccountCategory | undefined;
    if (variantType === FormVariant.EDIT && companyInfoData) {
      data = companyInfoData?.employerCategory;
    } else if (variantType === FormVariant.ENROLLMENT) {
      data = employerCategory;
    }
    const resolvedCategory = employerCategoriesData?.find(({ value }) => value === data?.category);

    reset({
      tier1: resolvedCategory?.groupName ?? resolvedCategory?.value,
      tier2: resolvedCategory?.groupName ? resolvedCategory?.value : '',
      tier3: data?.subCategory,
      tier4: data?.employeeVerificationType,
      ueiSAM: companyInfoData?.ueiSAM,
      dunsNumber: companyInfoData?.dunsNumber,
    });
  }, [companyInfoData, employerCategoriesData, employerCategory, reset, variantType]);

  const mapFormData = (formValues: FormValues): EmployerCategoryFormData => {
    const tier1Category = employerCategoriesData?.find(({ value }) => value === formValues.tier1);
    const tier2Category = employerCategoriesData?.find(({ value }) => value === formValues.tier2);
    const tier3Category = employerCategoriesData?.find(({ value }) => value === formValues.tier3);
    const tier4VerificationType = verificationTypesData?.find(({ value }) => value === formValues.tier4);

    const ueiSAMValue = formValues.ueiSAM ?? companyInfoData?.ueiSAM;
    const dunsNumberValue = formValues.dunsNumber ?? companyInfoData?.dunsNumber;

    // Mapped data scenario: ENROLLMENT
    if (FormVariant.ENROLLMENT) {
      return {
        employerCategory: {
          category: (tier2Category?.value ?? tier1Category?.value) as CategoryType,
          subCategory: tier3Category?.value as SubCategoryType,
          employeeVerificationType: tier4VerificationType?.value as VerificationEnumType,
        },
        ueiSAM: ueiSAMValue,
        dunsNumber: dunsNumberValue,
      } as EmployerCategoryFormData;
    }

    // Mapped data scenario: EDIT
    return {
      ...companyInfoData,
      employerCategory: {
        category: (tier2Category?.value ?? tier1Category?.value) as CategoryType,
        subCategory: tier3Category?.value as SubCategoryType,
        employeeVerificationType: tier4VerificationType?.value as VerificationEnumType,
      },
      ueiSAM: ueiSAMValue,
      dunsNumber: dunsNumberValue,
    } as EmployerCategoryFormData;
  };

  const handleSubmitForm = handleSubmit((data: FormValues) => {
    onSubmitForm(mapFormData(data));
  });

  return (
    <>
      {employerCategoriesIsLoading ? (
        <div className="margin-top-5 margin-bottom-5">
          Loading… <img src={LoaderIcon} alt="" className="height-2" />
        </div>
      ) : (
        <>
          {/* eslint-disable-next-line react/jsx-props-no-spreading */}
          <FormProvider {...formMethods}>
            <Form className="maxw-none" onSubmit={handleSubmitForm} noValidate>
              <FormGroup className="margin-top-4" error={Boolean(errors.tier1)}>
                <Fieldset>
                  <Legend className="maxw-none">
                    Which category best describes {displayCompanyName}? <Hint>(required)</Hint>
                  </Legend>
                  {errors.tier1 ? (
                    <ErrorMessage id="tier1-error" className="padding-bottom-0">
                      {errors.tier1.message}
                    </ErrorMessage>
                  ) : null}

                  {employerCategoriesData
                    ?.filter(({ parentValue }) => parentValue === undefined)
                    .sort(({ displayOrder: a }, { displayOrder: b }) => a - b)
                    .map(({ value, displayName }) => (
                      <IllustratedRadioButton
                        key={value}
                        id={`tier1-${paramCase(value)}`}
                        name="tier1"
                        label={
                          <>
                            {displayName}{' '}
                            <Button
                              variant="tertiary"
                              type="button"
                              id={`button-tier1-${paramCase(value)}`}
                              className="margin-left-1"
                              onClick={(): void => {
                                determineHelpModalText(value, 1);
                              }}
                              style={{ marginTop: 0 }}
                            >
                              <Icon
                                name="help"
                                title={{
                                  id: `help-tier1-${paramCase(value)}`,
                                  text: `Read more about the ${displayName} category in the help modal.`,
                                }}
                              />
                            </Button>
                            {value === 'NONE_OF_THE_ABOVE' && (
                              <Tag className="margin-left-2" variant="primary">
                                Most Common
                              </Tag>
                            )}
                          </>
                        }
                        aria-describedby={errors.tier1 ? 'tier1-error' : undefined}
                        value={value}
                        icon={<CategoryIcon icon={value} />}
                        aria-required="true"
                        ref={register({
                          required: {
                            value: true,
                            message: 'Category selection is required.',
                          },
                        })}
                        onChange={({ target }) => reset({ tier1: target.value })}
                      />
                    ))}

                  {tier1 && employerCategoriesData?.some(({ parentValue }) => parentValue === tier1) ? (
                    <FormGroup className="margin-top-4" error={Boolean(errors.tier2)}>
                      <Fieldset>
                        <Legend className="maxw-none">
                          What type of {employerCategoriesData?.find(({ value }) => value === tier1)?.displayName} is{' '}
                          {displayCompanyName}? <Hint>(required)</Hint>
                        </Legend>
                        {errors.tier2 ? (
                          <ErrorMessage id="tier2-error" className="padding-bottom-0">
                            {errors.tier2.message}
                          </ErrorMessage>
                        ) : null}

                        {employerCategoriesData
                          ?.filter(({ parentValue }) => parentValue === tier1)
                          .sort(({ displayOrder: a }, { displayOrder: b }) => a - b)
                          .map(({ value, displayName }) => (
                            <IllustratedRadioButton
                              key={value}
                              id={`tier2-${paramCase(value)}`}
                              name="tier2"
                              label={
                                <>
                                  {displayName}{' '}
                                  <Button
                                    variant="tertiary"
                                    type="button"
                                    id={`button-tier2-${paramCase(value)}`}
                                    className="margin-left-1"
                                    onClick={(): void => {
                                      determineHelpModalText(value, 2);
                                    }}
                                    style={{ marginTop: 0 }}
                                  >
                                    <Icon
                                      name="help"
                                      title={{
                                        id: `help-tier2-${paramCase(value)}`,
                                        text: `Read more about the ${displayName} category in the help modal.`,
                                      }}
                                    />
                                  </Button>
                                </>
                              }
                              aria-describedby={errors.tier2 ? 'tier2-error' : undefined}
                              value={value}
                              icon={<CategoryIcon icon={value} />}
                              aria-required="true"
                              ref={register({
                                required: {
                                  value: true,
                                  message: 'Category selection is required.',
                                },
                              })}
                              onChange={({ target }) => reset({ tier1, tier2: target.value })}
                            />
                          ))}

                        {tier2 && employerCategoriesData?.some(({ parentValue }) => parentValue === tier2) ? (
                          <FormGroup className="margin-top-4" error={Boolean(errors.tier3)}>
                            <Fieldset>
                              <Legend className="maxw-none">
                                What type of {employerCategoriesData?.find(({ value }) => value === tier2)?.displayName}{' '}
                                is {displayCompanyName}? <Hint>(required)</Hint>
                              </Legend>
                              {errors.tier3 ? (
                                <ErrorMessage id="tier3-error" className="padding-bottom-0">
                                  {errors.tier3.message}
                                </ErrorMessage>
                              ) : null}

                              {employerCategoriesData
                                ?.filter(({ parentValue }) => parentValue === tier2)
                                .sort(({ displayOrder: a }, { displayOrder: b }) => a - b)
                                .map(({ value, displayName }) => (
                                  <IllustratedRadioButton
                                    key={value}
                                    id={`tier3-${paramCase(value)}`}
                                    name="tier3"
                                    label={
                                      <>
                                        {displayName}{' '}
                                        <Button
                                          variant="tertiary"
                                          type="button"
                                          id={`button-tier3-${paramCase(value)}`}
                                          className="margin-left-1"
                                          onClick={(): void => {
                                            determineHelpModalText(value, 3);
                                          }}
                                          style={{ marginTop: 0 }}
                                        >
                                          <Icon
                                            name="help"
                                            title={{
                                              id: `help-tier3-${paramCase(value)}`,
                                              text: `Read more about the ${displayName} category in the help modal.`,
                                            }}
                                          />
                                        </Button>
                                      </>
                                    }
                                    aria-describedby={errors.tier3 ? 'tier3-error' : undefined}
                                    value={value}
                                    icon={<CategoryIcon icon={value} />}
                                    aria-required="true"
                                    ref={register({
                                      required: {
                                        value: true,
                                        message: 'Category selection is required.',
                                      },
                                    })}
                                    onChange={({ target }) => reset({ tier1, tier2, tier3: target.value })}
                                  />
                                ))}

                              {tier3 && !verificationTypesIsLoading && verificationTypesData?.length ? (
                                <FormGroup className="margin-top-4" error={Boolean(errors.tier4)}>
                                  <Fieldset>
                                    <Legend className="maxw-none">
                                      Which employees will {displayCompanyName} verify? <Hint>(required)</Hint>
                                    </Legend>
                                    {errors.tier4 ? (
                                      <ErrorMessage id="tier4-error" className="padding-bottom-0">
                                        {errors.tier4.message}
                                      </ErrorMessage>
                                    ) : null}

                                    {verificationTypesData
                                      .sort(({ displayOrder: a }, { displayOrder: b }) => a - b)
                                      .map(({ value, displayName }) => (
                                        <IllustratedRadioButton
                                          key={value}
                                          id={`tier4-${paramCase(value)}`}
                                          name="tier4"
                                          aria-describedby={errors.tier4 ? 'tier4-error' : undefined}
                                          label={
                                            <>
                                              {displayName}{' '}
                                              <Button
                                                variant="tertiary"
                                                type="button"
                                                id={`button-tier4-${paramCase(value)}`}
                                                className="margin-left-1"
                                                onClick={(): void => {
                                                  determineHelpModalText(value, 4);
                                                }}
                                                style={{ marginTop: 0 }}
                                              >
                                                <Icon
                                                  name="help"
                                                  title={{
                                                    id: `help-tier4-${paramCase(value)}`,
                                                    text: `Read more about the ${displayName} category in the help modal.`,
                                                  }}
                                                />
                                              </Button>
                                            </>
                                          }
                                          value={value}
                                          icon={<CategoryIcon icon={value} />}
                                          aria-required="true"
                                          ref={register({
                                            required: {
                                              value: true,
                                              message: 'Category selection is required.',
                                            },
                                          })}
                                        />
                                      ))}
                                  </Fieldset>
                                </FormGroup>
                              ) : null}
                            </Fieldset>
                          </FormGroup>
                        ) : null}
                      </Fieldset>
                    </FormGroup>
                  ) : null}
                </Fieldset>
              </FormGroup>
              <p className="margin-top-3 margin-bottom-4">
                Questions? We’re here to help! Email E-Verify support at <EVerifyEmail />.
              </p>
              {displayUeiSAM && <UEIField openModal={openModal} employerCategory={tier2 as CategoryType} />}
              {displayDunsNumber && <DUNSField openModal={openModal} />}
              {variantType === FormVariant.ENROLLMENT ? (
                <EnrollmentStepperButtons nextButtonType="submit" />
              ) : (
                <>
                  <Link to={cancelUrl || ''} className="usa-button usa-button--outline">
                    Cancel
                  </Link>
                  <Button type="submit" variant="primary">
                    Save
                  </Button>
                </>
              )}
            </Form>
          </FormProvider>
          <HelpModal
            modalContent={modalContent}
            open={helpModalIsOpen}
            close={(): void => {
              setHelpModalIsOpen(false);
            }}
          />
        </>
      )}
    </>
  );
};

export default EmployerCategoryForm;
